import { useEffect, useId, useState } from "react";
import { common } from "../copy/common.js";
import { settings as copy } from "../copy/settings.js";
import { updateSettings, useSettings, type SpeechRate, type TextSize } from "../lib/settings.js";
import { canSpeak, voiceFor } from "../lib/text.js";
import { useSpeak } from "./SpeakButton.js";

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Record<T, string>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex gap-1 rounded-2xl border border-line bg-paper-2 p-1"
    >
      {(Object.keys(options) as T[]).map((key) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={`min-h-12 flex-1 rounded-xl px-2 text-sm font-semibold transition-colors ${
            value === key ? "bg-paper-3 text-ink shadow-(--shadow-card)" : "text-ink-soft hover:text-ink"
          }`}
        >
          {options[key]}
        </button>
      ))}
    </div>
  );
}

function Switch({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const hintId = useId();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-describedby={hintId}
      onClick={() => onChange(!checked)}
      className="flex min-h-12 w-full items-center justify-between gap-4 rounded-2xl border border-line bg-paper-3/60 px-4 py-3 text-left"
    >
      <span>
        <span className="block font-semibold">{label}</span>
        <span id={hintId} className="block text-sm text-ink-soft">
          {hint}
        </span>
      </span>
      {/* The state is also in the knob position, never in colour alone (docs/UX.md §8). */}
      <span
        aria-hidden
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${checked ? "bg-moss" : "bg-ink-soft/30"}`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${checked ? "left-6" : "left-1"}`}
        />
      </span>
    </button>
  );
}

/** Voices load late in Chrome and Safari; re-check once they arrive. */
function useMissingVoices(): Array<"en" | "fr"> {
  const check = () => (["en", "fr"] as const).filter((lang) => canSpeak() && !voiceFor(lang));
  const [missing, setMissing] = useState<Array<"en" | "fr">>([]);
  useEffect(() => {
    if (!canSpeak()) return;
    const update = () => {
      // An empty list means "not loaded yet", not "no voices".
      if (window.speechSynthesis.getVoices().length > 0) setMissing(check());
    };
    update();
    window.speechSynthesis.addEventListener?.("voiceschanged", update);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", update);
  }, []);
  return missing;
}

/** A voice sample; lit while it reads, like the speaker on a card (#98). */
function SampleButton({ lang, onTry }: { lang: "en" | "fr"; onTry: () => void }) {
  const { speaking, say } = useSpeak();
  return (
    <button
      type="button"
      lang={lang}
      data-speaking={speaking || undefined}
      onClick={() => {
        onTry();
        say(copy.sample[lang], lang);
      }}
      className={`min-h-12 flex-1 rounded-2xl border-2 px-4 font-semibold hover:border-fox/50 ${
        speaking ? "border-fox bg-fox/10 text-fox-ink" : "border-line bg-paper-3/60"
      }`}
    >
      <span lang="de">{copy.tryVoice(common.lang[lang])}</span>
    </button>
  );
}

export function SettingsPanel() {
  const current = useSettings();
  const missing = useMissingVoices();
  // The hint answers a silence the page cannot hear, so it waits for a try
  // instead of reading like an error on every visit (#98).
  const [tried, setTried] = useState(false);

  return (
    <section className="space-y-5 sm:max-w-md">
      <div>
        <h2 className="text-xl">{copy.title}</h2>
        <p className="text-sm text-ink-soft">{copy.deviceHint}</p>
      </div>

      <div className="space-y-2">
        <h3 className="text-base font-semibold">{copy.textSize}</h3>
        <Segmented<TextSize>
          label={copy.textSize}
          options={copy.textSizes}
          value={current.textSize}
          onChange={(textSize) => updateSettings({ textSize })}
        />
      </div>

      <Switch
        label={copy.readableFont}
        hint={copy.readableFontHint}
        checked={current.readableFont}
        onChange={(readableFont) => updateSettings({ readableFont })}
      />

      <div className="space-y-3">
        <h3 className="text-base font-semibold">{copy.speech}</h3>
        {canSpeak() ? (
          <>
            <Switch
              label={copy.autoSpeak}
              hint={copy.autoSpeakHint}
              checked={current.autoSpeak}
              onChange={(autoSpeak) => updateSettings({ autoSpeak })}
            />
            <Segmented<SpeechRate>
              label={copy.rate}
              options={copy.rates}
              value={current.speechRate}
              onChange={(speechRate) => updateSettings({ speechRate })}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              {(["en", "fr"] as const).map((lang) => (
                <SampleButton key={lang} lang={lang} onTry={() => setTried(true)} />
              ))}
            </div>
            {missing.map((lang) => (
              <p key={lang} className="text-sm text-ink-soft">
                {copy.noVoice(common.lang[lang])}
              </p>
            ))}
            {/* No live region: it would talk over the sample being read, and
                the line sits right under the buttons. */}
            {tried ? <p className="text-sm text-ink-soft">{copy.noSound}</p> : null}
          </>
        ) : (
          <p className="text-sm text-ink-soft">{copy.noSpeech}</p>
        )}
      </div>
    </section>
  );
}
